/**
 * GroundingFallbackBanner - shows when grounding image couldn't be used.
 *
 * Displays a subtle DENiZEN-styled info message when the backend fell back
 * to text-to-image because the grounding image failed to load or FLUX
 * /edits failed and fell back to /generations.
 *
 * Auto-dismisses after 8 seconds or on user click.
 */

import { useState, useEffect } from 'react'

interface GroundingFallbackBannerProps {
  groundingApplied?: boolean | null
  generationMode?: string | null
  /** Used as reset key — when this changes, dismissed state resets */
  roundCount?: number
}

export function GroundingFallbackBanner({
  groundingApplied,
  generationMode,
  roundCount = 0,
}: GroundingFallbackBannerProps) {
  const [dismissed, setDismissed] = useState(false)

  // Show when grounding was requested but not applied, or when fallback was used
  const shouldShow =
    !dismissed &&
    (groundingApplied === false || generationMode === 'text-to-image-fallback')

  // Auto-dismiss after 8 seconds
  useEffect(() => {
    if (!shouldShow) return

    const timer = setTimeout(() => setDismissed(true), 8000)
    return () => clearTimeout(timer)
  }, [shouldShow])

  // Reset dismissed state when a new round arrives (avoids stale dismiss on retry)
  useEffect(() => {
    setDismissed(false)
  }, [roundCount])

  if (!shouldShow) return null

  const message =
    generationMode === 'text-to-image-fallback'
      ? "The location photo couldn't be incorporated this time. Your image was created from the conversation instead."
      : "We couldn't use the location photo this time. Your image was created from the conversation instead."

  return (
    <div
      className="denizen-card mb-4 flex items-start gap-3 border-l-4 border-[#39b0a3] bg-[#fff8eb] px-4 py-3 text-sm text-[#1a1a1a]"
      role="status"
      aria-live="polite"
    >
      <svg
        className="mt-0.5 h-4 w-4 shrink-0 text-[#39b0a3]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
        strokeWidth={2}
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <span>{message}</span>
      <button
        onClick={() => setDismissed(true)}
        className="ml-auto shrink-0 text-[#39b0a3] hover:text-[#216732] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#40aa3b]"
        aria-label="Dismiss notification"
      >
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </div>
  )
}
